/**
 * ChallengeResults Page
 *
 * Shows the structure analysis feedback for a submitted challenge.
 * Includes the story content, checklist, and suggestions.
 */

import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  Target,
  RefreshCw,
  BookOpen,
  Loader2,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { StructureChecklist, ScoreDisplay } from '@/components/challenge'
import { useToast } from '@/context/ToastContext'
import { getSubmissionWithChallenge } from '@/services/challengeService'
import { calculateStoryScore } from '@/services/structureAnalyzer'
import type { StoryChallenge, ChallengeSubmission } from '@/types/database'
import type { StructureAnalysisResult } from '@/types/domain'

export function ChallengeResults() {
  const { submissionId } = useParams<{
    challengeId: string
    submissionId: string
  }>()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [challenge, setChallenge] = useState<StoryChallenge | null>(null)
  const [submission, setSubmission] = useState<ChallengeSubmission | null>(null)
  const [analysis, setAnalysis] = useState<StructureAnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showFullStory, setShowFullStory] = useState(false)

  // Load submission and challenge
  useEffect(() => {
    async function loadData() {
      if (!submissionId) return

      setIsLoading(true)
      try {
        const result = await getSubmissionWithChallenge(submissionId)
        if (!result) {
          showToast('Submission not found', 'error')
          navigate('/challenge')
          return
        }

        setChallenge(result.challenge)
        setSubmission(result.submission)

        // Parse analysis result
        if (result.submission.analysis_result) {
          setAnalysis(result.submission.analysis_result as unknown as StructureAnalysisResult)
        }
      } catch (error) {
        console.error('Error loading results:', error)
        showToast('Failed to load results', 'error')
        navigate('/challenge')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [submissionId, navigate, showToast])

  if (isLoading || !challenge || !submission || !analysis) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
      </div>
    )
  }

  const score = calculateStoryScore(analysis)

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/challenge">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-display font-bold text-lg text-[var(--color-kids-text)]">
                  Your Feedback
                </h1>
                <p className="text-sm text-[var(--color-kids-text-muted)]">
                  {challenge.title}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card variant="elevated">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <ScoreDisplay score={score} />
                <div className="flex-1 text-center md:text-left">
                  <h2 className="font-display font-bold text-xl text-[var(--color-kids-text)] mb-2">
                    {score >= 80
                      ? 'Amazing work!'
                      : score >= 60
                        ? 'Great job!'
                        : score >= 40
                          ? 'Good effort!'
                          : 'Keep practicing!'}
                  </h2>
                  <p className="text-[var(--color-kids-text-muted)]">
                    {score >= 80
                      ? 'Your story has excellent structure. You included most of the key elements!'
                      : score >= 60
                        ? 'Your story is shaping up nicely. A few tweaks could make it even better.'
                        : score >= 40
                          ? "You're on the right track. Try adding more story elements next time."
                          : 'Every story is a chance to learn. Check the suggestions below!'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Structure Checklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6">
              <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                Story Structure Check
              </h3>
              <StructureChecklist analysis={analysis} mode="coach" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Your Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)]">
                  Your Story
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullStory(!showFullStory)}
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  {showFullStory ? 'Show Less' : 'Show Full Story'}
                </Button>
              </div>
              <div
                className={`
                  text-[var(--color-kids-text)] leading-relaxed
                  ${showFullStory ? '' : 'line-clamp-4'}
                `}
              >
                {submission.content.split('\n').map((paragraph, i) => (
                  <p key={i} className="mb-2">
                    {paragraph}
                  </p>
                ))}
              </div>
              <p className="text-sm text-[var(--color-kids-text-muted)] mt-3">
                {submission.word_count} words
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Link to={`/revise/submission/${submission.id}`} className="flex-1">
            <Button variant="primary" size="lg" className="w-full">
              <RefreshCw className="w-5 h-5 mr-2" />
              Revise This Story
            </Button>
          </Link>
          <Link to="/challenge" className="flex-1">
            <Button variant="secondary" size="lg" className="w-full">
              <Target className="w-5 h-5 mr-2" />
              Try Another Challenge
            </Button>
          </Link>
        </motion.div>

        {/* Tips Card */}
        {analysis.suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-[var(--color-kids-primary)]/5 border-[var(--color-kids-primary)]/20">
              <CardContent className="p-6">
                <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-3">
                  Tips for Your Next Story
                </h3>
                <ul className="space-y-2">
                  {analysis.suggestions.slice(0, 3).map((suggestion, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[var(--color-kids-text)]"
                    >
                      <span className="text-[var(--color-kids-primary)]">•</span>
                      {suggestion.message}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  )
}

export default ChallengeResults
